/**
 * Client-side image compression and thumbnail generation using Canvas API.
 * No external dependencies needed.
 */

/**
 * Hard upper bound for any single image we accept for upload.
 *
 * Google Shopping rejects images larger than 16 MB and Meta rejects images
 * larger than 8 MB. We use the stricter feed limit (16 MB) as the absolute
 * ceiling at upload time so a single source file can never make it into
 * storage and from there into either ad feed.
 */
export const MAX_IMAGE_UPLOAD_BYTES = 16 * 1024 * 1024;

/**
 * MIME types we accept for car/hero imagery. Both Google Shopping and Meta
 * Catalog explicitly reject SVG and exotic vector formats, so we restrict to
 * widely supported raster formats only — even though the browser would happily
 * report `image/svg+xml` as an `image/*` type.
 */
export const ALLOWED_IMAGE_MIME_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
  "image/avif",
  "image/gif",
  "image/heic",
  "image/heif",
] as const;

export type ImageValidationError =
  | { kind: "not_image"; message: string }
  | { kind: "unsupported_type"; message: string; mimeType: string }
  | { kind: "too_large"; message: string; sizeBytes: number; limitBytes: number };

/**
 * Validate a file before uploading it as a car/feed image.
 *
 * Returns `null` when the file passes; otherwise returns a structured error
 * with a Danish UI message. Callers should surface `error.message` via
 * toast and skip the file entirely.
 */
export function validateImageFile(file: File): ImageValidationError | null {
  const mime = (file.type || "").toLowerCase();

  if (!mime.startsWith("image/")) {
    return {
      kind: "not_image",
      message: `"${file.name}" er ikke et billede (type: ${file.type || "ukendt"}).`,
    };
  }

  if (!ALLOWED_IMAGE_MIME_TYPES.includes(mime as typeof ALLOWED_IMAGE_MIME_TYPES[number])) {
    return {
      kind: "unsupported_type",
      mimeType: mime,
      message: `"${file.name}" har et format (${mime}) som ikke understøttes af Google Shopping eller Meta-feeds. Brug JPG, PNG, WebP eller AVIF.`,
    };
  }

  if (file.size > MAX_IMAGE_UPLOAD_BYTES) {
    const sizeMb = (file.size / (1024 * 1024)).toFixed(1);
    return {
      kind: "too_large",
      sizeBytes: file.size,
      limitBytes: MAX_IMAGE_UPLOAD_BYTES,
      message: `"${file.name}" er ${sizeMb} MB — over den maksimale grænse på 16 MB for ad-feeds.`,
    };
  }

  return null;
}

/**
 * Validate a batch of files. Returns the accepted files plus a list of
 * rejection reasons so the caller can show a single summary toast.
 */
export function validateImageFiles(files: File[] | FileList): {
  accepted: File[];
  rejected: Array<{ file: File; error: ImageValidationError }>;
} {
  const accepted: File[] = [];
  const rejected: Array<{ file: File; error: ImageValidationError }> = [];
  for (const file of Array.from(files)) {
    const error = validateImageFile(file);
    if (error) rejected.push({ file, error });
    else accepted.push(file);
  }
  return { accepted, rejected };
}

interface CompressOptions {
  maxWidth: number;
  maxHeight: number;
  quality: number; // 0-1
  type?: string;   // output mime type
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
}

function resizeToCanvas(
  img: HTMLImageElement,
  maxWidth: number,
  maxHeight: number,
): HTMLCanvasElement {
  let { naturalWidth: w, naturalHeight: h } = img;

  if (w > maxWidth || h > maxHeight) {
    const ratio = Math.min(maxWidth / w, maxHeight / h);
    w = Math.round(w * ratio);
    h = Math.round(h * ratio);
  }

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(img, 0, 0, w, h);
  return canvas;
}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  type: string,
  quality: number,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Canvas toBlob failed"))),
      type,
      quality,
    );
  });
}

/**
 * Compress an image file using the browser Canvas API.
 * Returns a webp blob for best compression.
 */
export async function compressImage(
  file: File,
  opts: Partial<CompressOptions> = {},
): Promise<Blob> {
  const { maxWidth = 1920, maxHeight = 1440, quality = 0.82, type = "image/webp" } = opts;

  const img = await loadImage(file);
  const canvas = resizeToCanvas(img, maxWidth, maxHeight);
  URL.revokeObjectURL(img.src);

  const blob = await canvasToBlob(canvas, type, quality);
  // If compressed is larger than original (rare for small images), return original
  return blob.size < file.size ? blob : file;
}

/**
 * Generate a small thumbnail (400px wide) for grid/list views.
 */
export async function generateThumbnail(
  file: File,
  opts: Partial<CompressOptions> = {},
): Promise<Blob> {
  const { maxWidth = 400, maxHeight = 300, quality = 0.7, type = "image/webp" } = opts;

  const img = await loadImage(file);
  const canvas = resizeToCanvas(img, maxWidth, maxHeight);
  URL.revokeObjectURL(img.src);

  return canvasToBlob(canvas, type, quality);
}

/**
 * Process a single image file: compress the main image and generate a thumbnail.
 * Returns { main, thumbnail } blobs.
 */
export async function processImage(file: File): Promise<{
  main: Blob;
  thumbnail: Blob;
  originalSize: number;
  compressedSize: number;
  thumbnailSize: number;
}> {
  const [main, thumbnail] = await Promise.all([
    compressImage(file),
    generateThumbnail(file),
  ]);

  return {
    main,
    thumbnail,
    originalSize: file.size,
    compressedSize: main.size,
    thumbnailSize: thumbnail.size,
  };
}
